import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Star,
  Eye,
  EyeOff,
  Trash2,
  Mail,
  MessageSquare,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(d) {
  try {
    return new Date(d).toLocaleString("de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function StarRow({ rating, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          strokeWidth={1.5}
          className={
            n <= rating
              ? "fill-[#1a1918] text-[#1a1918]"
              : "text-[#c8c2bb]"
          }
        />
      ))}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, hint, testId, accent }) {
  return (
    <div
      data-testid={testId}
      className="rounded-3xl border border-warm bg-white p-6 transition-all hover:-translate-y-0.5"
    >
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        <div
          className={cn(
            "rounded-full p-2",
            accent === "warn" ? "bg-red-50 text-red-600" : "bg-cream text-brand"
          )}
        >
          <Icon size={16} strokeWidth={1.75} />
        </div>
      </div>
      <p className="font-display mt-4 text-4xl font-bold text-brand tabular-nums">
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-[#5c5752]">{hint}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reviewFilter, setReviewFilter] = useState("all"); // all | published | hidden
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState("reviews"); // reviews | messages

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, r, m] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/reviews`, { withCredentials: true }),
        axios.get(`${API}/admin/contact-messages`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setReviews(r.data);
      setMessages(m.data);
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 401) {
        toast.error("Sitzung abgelaufen, bitte erneut anmelden.");
        await logout();
        navigate("/admin", { replace: true });
      } else {
        toast.error("Daten konnten nicht geladen werden.");
      }
    } finally {
      setRefreshing(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    if (user) loadAll();
  }, [user, loadAll]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <p className="text-sm text-[#5c5752]">Lade…</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin" replace />;
  }

  const handleLogout = async () => {
    await logout();
    toast.success("Abgemeldet.");
    navigate("/admin", { replace: true });
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/reviews/${id}`,
        { status },
        { withCredentials: true }
      );
      toast.success(
        status === "published"
          ? "Bewertung veröffentlicht."
          : "Bewertung versteckt."
      );
      loadAll();
    } catch (e) {
      console.error(e);
      toast.error("Konnte Status nicht ändern.");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Diese Bewertung wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, {
        withCredentials: true,
      });
      toast.success("Bewertung gelöscht.");
      loadAll();
    } catch (e) {
      console.error(e);
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Diese Nachricht wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/admin/contact-messages/${id}`, {
        withCredentials: true,
      });
      toast.success("Nachricht gelöscht.");
      loadAll();
    } catch (e) {
      console.error(e);
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const filteredReviews = reviews.filter((r) =>
    reviewFilter === "all" ? true : r.status === reviewFilter
  );

  return (
    <div
      className="min-h-screen bg-cream"
      data-testid="admin-dashboard"
    >
      {/* Header */}
      <header className="sticky top-0 z-20 glass-nav">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 md:px-10">
          <div>
            <span className="eyebrow">Admin Dashboard</span>
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-brand md:text-3xl">
              Scoops&Co
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadAll}
              disabled={refreshing}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-full border border-warm bg-white px-4 py-2 text-sm font-medium text-brand transition-all hover:border-brand disabled:opacity-50"
            >
              <RefreshCw
                size={14}
                className={refreshing ? "animate-spin" : ""}
              />
              Aktualisieren
            </button>
            <span className="hidden text-sm text-[#5c5752] md:inline">
              {user.email}
            </span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-medium text-white transition-all hover:bg-[#3a3836]"
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-12 md:px-10">
        {/* Stats */}
        <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard
            label="Bewertungen gesamt"
            value={stats?.reviews_total ?? "—"}
            icon={MessageSquare}
            testId="stat-reviews-total"
          />
          <StatCard
            label="Veröffentlicht"
            value={stats?.reviews_published ?? "—"}
            icon={CheckCircle2}
            hint="Auf der Webseite sichtbar"
            testId="stat-reviews-published"
          />
          <StatCard
            label="Versteckt"
            value={stats?.reviews_hidden ?? "—"}
            icon={AlertTriangle}
            hint="Nur hier sichtbar"
            testId="stat-reviews-hidden"
            accent={stats?.reviews_hidden > 0 ? "warn" : undefined}
          />
          <StatCard
            label="Ø Bewertung"
            value={
              stats
                ? String(stats.reviews_average_rating).replace(".", ",")
                : "—"
            }
            icon={TrendingUp}
            hint="Alle Bewertungen"
            testId="stat-reviews-average"
          />
        </section>

        {/* Tabs */}
        <div className="mt-12 flex flex-wrap items-center gap-3 border-b border-warm">
          <button
            data-testid="tab-reviews"
            onClick={() => setTab("reviews")}
            className={cn(
              "relative pb-4 pr-6 text-sm font-medium transition-colors",
              tab === "reviews"
                ? "text-brand"
                : "text-[#5c5752] hover:text-brand"
            )}
          >
            Bewertungen ({reviews.length})
            {tab === "reviews" && (
              <span className="absolute bottom-0 left-0 h-[2px] w-full bg-brand" />
            )}
          </button>
          <button
            data-testid="tab-messages"
            onClick={() => setTab("messages")}
            className={cn(
              "relative pb-4 pr-6 text-sm font-medium transition-colors",
              tab === "messages"
                ? "text-brand"
                : "text-[#5c5752] hover:text-brand"
            )}
          >
            Kontakt-Nachrichten ({messages.length})
            {tab === "messages" && (
              <span className="absolute bottom-0 left-0 h-[2px] w-full bg-brand" />
            )}
          </button>
        </div>

        {/* Reviews tab */}
        {tab === "reviews" && (
          <section className="mt-8">
            <div className="mb-6 flex flex-wrap gap-2">
              {[
                { id: "all", label: "Alle" },
                { id: "published", label: "Veröffentlicht" },
                { id: "hidden", label: "Versteckt" },
              ].map((f) => (
                <button
                  key={f.id}
                  data-testid={`review-filter-${f.id}`}
                  onClick={() => setReviewFilter(f.id)}
                  className={cn(
                    "rounded-full px-4 py-1.5 text-xs font-medium transition-all",
                    reviewFilter === f.id
                      ? "bg-brand text-white"
                      : "border border-warm text-brand hover:border-brand"
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {filteredReviews.length === 0 ? (
              <p className="rounded-3xl border border-warm bg-white px-6 py-10 text-center text-sm text-[#5c5752]">
                Keine Bewertungen in dieser Ansicht.
              </p>
            ) : (
              <ul
                data-testid="admin-reviews-list"
                className="grid grid-cols-1 gap-4 md:grid-cols-2"
              >
                {filteredReviews.map((r) => (
                  <li
                    key={r.id}
                    data-testid={`admin-review-${r.id}`}
                    className={cn(
                      "rounded-3xl border bg-white p-6 transition-all",
                      r.status === "hidden"
                        ? "border-red-200"
                        : "border-warm"
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-lg font-semibold text-brand">
                          {r.name}
                        </p>
                        <div className="mt-1 flex items-center gap-2">
                          <StarRow rating={r.rating} />
                          <span className="text-xs text-[#5c5752]">
                            {formatDate(r.created_at)}
                          </span>
                        </div>
                      </div>
                      <span
                        className={cn(
                          "rounded-full px-3 py-1 text-[10px] uppercase tracking-wider font-medium",
                          r.status === "published"
                            ? "bg-green-50 text-green-700"
                            : "bg-red-50 text-red-700"
                        )}
                      >
                        {r.status === "published" ? "Öffentlich" : "Versteckt"}
                      </span>
                    </div>

                    <p className="mt-4 text-sm leading-relaxed text-brand">
                      „{r.comment}"
                    </p>

                    <div className="mt-5 flex flex-wrap gap-2 border-t border-warm pt-4">
                      {r.status === "hidden" ? (
                        <button
                          onClick={() => updateStatus(r.id, "published")}
                          data-testid={`publish-review-${r.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-warm bg-white px-3 py-1.5 text-xs font-medium text-brand transition-all hover:border-brand"
                        >
                          <Eye size={12} />
                          Veröffentlichen
                        </button>
                      ) : (
                        <button
                          onClick={() => updateStatus(r.id, "hidden")}
                          data-testid={`hide-review-${r.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-warm bg-white px-3 py-1.5 text-xs font-medium text-brand transition-all hover:border-brand"
                        >
                          <EyeOff size={12} />
                          Verstecken
                        </button>
                      )}
                      <button
                        onClick={() => deleteReview(r.id)}
                        data-testid={`delete-review-${r.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition-all hover:bg-red-50"
                      >
                        <Trash2 size={12} />
                        Löschen
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Messages tab */}
        {tab === "messages" && (
          <section className="mt-8">
            {messages.length === 0 ? (
              <p className="rounded-3xl border border-warm bg-white px-6 py-10 text-center text-sm text-[#5c5752]">
                Keine Nachrichten bisher.
              </p>
            ) : (
              <ul
                data-testid="admin-messages-list"
                className="space-y-4"
              >
                {messages.map((m) => (
                  <li
                    key={m.id}
                    data-testid={`admin-message-${m.id}`}
                    className="rounded-3xl border border-warm bg-white p-6"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-lg font-semibold text-brand">
                          {m.name}
                        </p>
                        <a
                          href={`mailto:${m.email}`}
                          className="mt-1 inline-flex items-center gap-1.5 text-xs text-[#5c5752] link-underline"
                        >
                          <Mail size={12} /> {m.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-[#5c5752]">
                          {formatDate(m.created_at)}
                        </span>
                        <button
                          onClick={() => deleteMessage(m.id)}
                          data-testid={`delete-message-${m.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition-all hover:bg-red-50"
                        >
                          <Trash2 size={12} />
                          Löschen
                        </button>
                      </div>
                    </div>
                    <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-brand">
                      {m.message}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
